/**
 * Online protokoll – a kliens és a szoba-szerver közös üzenettípusai.
 *
 * A verseny szimulációja a gazdánál fut (host-authoritative). A kliensek csak a
 * saját bemenetüket küldik (30 Hz), a gazda 20 Hz-en küld állapot-pillanatképet.
 * A rövid mezőnevek szándékosak: a snapshot másodpercenként többször megy ki.
 */
import type { ItemId, OnlineMatchInfo, OnlineSeat, SurfaceKind } from '../core/types';

export type { OnlineHumanInfo, OnlineMatchInfo, OnlineSeat } from '../core/types';

export const MAX_ONLINE_PLAYERS = 4;
/** A mezőny mérete: 4 ember + 2 AI, vagy 6 ember lenne a teljes rács. */
export const GRID_SIZE = 6;
export const SNAPSHOT_HZ = 20;
export const INPUT_HZ = 30;
/** Ennyivel a friss snapshot mögött renderelünk (simítja a hálózati ingadozást). */
export const INTERP_DELAY = 0.12;
export const PROTOCOL_VERSION = 1;

export type OnlinePhase = 'lobby' | 'racing' | 'results';

export interface OnlineSettings {
  trackId: string;
  night: boolean;
  ai: boolean;
  laps: number;
}

export interface OnlineYou {
  id: string;
  slot: number;
  host: boolean;
}

export interface OnlinePlayerView {
  id: string;
  name: string;
  character: string | null;
  slot: number;
  host: boolean;
}

export interface OnlineRoomView {
  code: string;
  phase: OnlinePhase;
  hostId: string;
  maxPlayers: number;
  you: OnlineSeat;
  settings: OnlineSettings;
  players: OnlinePlayerView[];
}


/** Tömör bemenet: kormány, gáz, fék, drift, tárgy, képesség. */
export interface InputWire {
  s: number;
  t: number;
  b: number;
  d: boolean;
  u: boolean;
  a: boolean;
}

export interface KartSnap {
  /** kart id */
  i: number;
  x: number;
  y: number;
  h: number;
  vx: number;
  vy: number;
  /** kör, haladás, teljes haladás */
  l: number;
  p: number;
  tp: number;
  /** tárgy + rulett */
  it: ItemId | null;
  rt: number;
  /** a rulett teljes hossza (a HUD arányához) */
  rtt: number;
  /** legjobb kör (mp) */
  bl: number | null;
  /** boost, slow, slowFactor, spin, spinDir, shield */
  bo: number;
  sl: number;
  sf: number;
  sp: number;
  sd: number;
  sh: number;
  /** drift */
  da: 0 | 1;
  dc: number;
  dd: number;
  /** felület, képesség-cooldown, hop, kiesés, respawn */
  su: SurfaceKind;
  ac: number;
  hp: number;
  o: 0 | 1;
  rs: number;
  /** célba ért */
  f: 0 | 1;
  ft: number;
  lt: number;
}

export interface BumpWire {
  x: number;
  y: number;
  s: number;
}

export interface Snapshot {
  /** versenyidő, mp */
  tm: number;
  ph: 'countdown' | 'racing' | 'finished';
  cd: number;
  go: number;
  ks: KartSnap[];
  /** dobozok: [aktív, visszatöltés] – a pozíció a pályából jön */
  bx: [0 | 1, number][];
  /** csapdák: [id, fajta, x, y, sugár, ownerId, élet] */
  hz: [number, ItemId, number, number, number, number, number][];
  /** lövedékek: [id, fajta, x, y, vx, vy, sugár, ownerId, élet] */
  pr: [number, ItemId, number, number, number, number, number, number, number][];
  /** helyezési sorrend kart id-kkel */
  ord: number[];
  /** az utolsó pillanatkép óta történt item-események */
  ev: ItemEventWire[];
  /** karambolok (hang + szikra) */
  bp: BumpWire[];
  /** ördögszekerek: [x, y, sugár, szög] */
  tw?: [number, number, number, number][];
}

export interface ItemEventWire {
  k: 'pickup' | 'roll' | 'use' | 'hit' | 'blocked' | 'explode' | 'bounce';
  i: ItemId;
  x: number;
  y: number;
  o?: number;
  t?: number;
}

export type ClientMessage =
  | { t: 'ping'; ts: number }
  | { t: 'create'; name: string; character?: string | null; settings?: Partial<OnlineSettings> }
  | { t: 'join'; code: string; name: string; character?: string | null }
  | { t: 'pick'; character: string }
  | { t: 'settings'; settings: Partial<OnlineSettings> }
  | { t: 'start'; picks: string[] }
  | { t: 'input'; input: InputWire }
  | { t: 'snapshot'; s: Snapshot }
  | { t: 'events'; e: ItemEventWire[] }
  | { t: 'pause'; paused: boolean }
  | { t: 'raceEnd'; result: unknown }
  | { t: 'toLobby' }
  | { t: 'leave' };

export interface ServerMessageMap {
  room: { t: 'room'; room: OnlineRoomView };
  start: { t: 'start'; match: OnlineMatchInfo };
  input: { t: 'input'; from: string; slot: number; input: InputWire };
  snapshot: { t: 'snapshot'; s: Snapshot };
  events: { t: 'events'; e: ItemEventWire[] };
  pause: { t: 'pause'; paused: boolean };
  result: { t: 'result'; result: unknown };
  left: { t: 'left'; id: string; slot: number; reason: string };
  closed: { t: 'closed'; reason: string; message: string };
  'left-room': { t: 'left-room' };
  pong: { t: 'pong'; ts: number };
  error: { t: 'error'; code: string; message: string };
}

export type ServerMessage = ServerMessageMap[keyof ServerMessageMap];

export const emptyWireInput = (): InputWire => ({ s: 0, t: 0, b: 0, d: false, u: false, a: false });
